import styled from '@emotion/styled';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens, Text, Loader, Button } from '@hotjar/hotsauce';
import { useWindowTitleWithBlur } from '@hotjar/webapp/hooks/useWindowTitle';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { showContent as showAppcuesContent } from '@hotjar/webapp/tracking/tools/appcues';
import { DEMO_ACCOUNT_DAHSBOARD_ONBOARDING_ID } from '@hotjar/webapp/constants/appcues';
import { ErrorBoundary } from '@hotjar/error-handler';
import { useNavigation } from '@hotjar/navigation';
import { useAccountExperiment } from '@hotjar/webapp/hooks/useExperiment';
import { CMS } from '@hotjar/webapp/constants/integration-guides';
import { useTrackingCodeState } from '@hotjar/webapp/hooks/useTrackingCode';
import { isDemoSiteId } from '@hotjar/webapp/domains/acquisition/demo/demo-site-redirections';
import { useGetBeamerPosts } from '@hotjar/webapp/features/ProductUpdates/api';
import {
  RecordingsCard,
  FeedbackCard,
  SurveysCard,
  HeatmapsCard,
  usePostInstallView,
  PostInstallCard,
  FaqsCard,
  ProductDemoCard,
} from './components';
import { SuggestionsBox } from './components/SuggestionsBox';

import { SpacesListSidebar as TrackingAndActivity } from '../SpacesList/Sidebar';
import { TrackingCodeCard } from './components/TrackingCodeCard';
import { QuickLinksSection } from './components/QuickLinksSection';
import { CmsCard } from './components/CmsCard';
import { useSetupWizard } from '../SetupWizard/hooks/useSetupWizard';
import { DemoSiteAlertBanner } from './components/DemoSiteAlertBanner';
import { NewFeaturesSection } from './components/NewFeaturesSection';
import { HotjarTipsSection } from './components/HotjarTipsSection';
import { GuidedSetupCard } from './components/GuidedSetupCard';
import { ProductTourGuideCard } from './components/ProductTourGuideCard';
import { SharingModule } from '../../../components/SharingModule/SharingModule';
import { FeaturedInsights } from './components/FeaturedInsights';

const SIDEBAR_WIDTH = '320px';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
`;

const Content = styled.div`
  flex-grow: 1;
`;

const InnerContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${tokens.spacer.x1pt5} ${tokens.spacer.x3};
  padding: 0 ${tokens.spacer.x5} ${tokens.spacer.x4} ${tokens.spacer.x5};
  margin: 0 auto ${tokens.spacer.x4} auto;
  max-width: 1600px;
  width: 100%;
`;

export const FullContentWidth = styled.div`
  grid-column: span 2;
`;

const Column = styled.div`
  grid-column: span 2;

  & > div {
    margin-bottom: ${tokens.spacer.x6};
  }

  @media ${tokens.breakpoint.minWidth.xxl} {
    grid-column: unset;
  }
`;

const TCSectionWrapper = styled.div`
  margin-bottom: ${tokens.spacer.x6};
`;

const StyledSideBar = styled.aside<{ offset: number }>`
  top: ${({ offset }) => offset}px;
  right: 0;
  bottom: 0;
  width: ${SIDEBAR_WIDTH};
  flex: 0 0 ${SIDEBAR_WIDTH};
  background-color: ${tokens.color.ui_background};
  border-left: 1px solid ${tokens.color.ui_border_light};
  overflow-y: auto;
`;

const HeaderWrapper = styled.div`
  grid-column: span 2;
  margin: ${tokens.spacer.x4} 0 ${tokens.spacer.x2};
  display: grid;
  grid-template-columns: 1fr auto;
`;

const Sidebar = () => {
  const { topBarHeight } = useNavigation();

  return (
    <StyledSideBar offset={topBarHeight}>
      <TrackingAndActivity position="Overview" />
    </StyledSideBar>
  );
};

export function Overview(): React.ReactElement | null {
  const { t, i18n } = useTranslation();
  const { data: currentSite } = useCurrentSite();
  const { data: currentUser } = useCurrentUser();
  const currentSiteId = currentSite?.id;
  const postInstallCardVisible = usePostInstallView(currentSite?.id);
  const { installed: isTrackingCodeInstalled } = useTrackingCodeState();
  const { cms: detectedCms, gtm } = useSetupWizard();
  const platformDetected =
    gtm.detected || (detectedCms && Object.values(CMS).includes(detectedCms));

  // TODO: update translations to remove workspace related content
  useWindowTitleWithBlur(
    t('activation:space-overview.title'),
    t('activation:space-overview.title'),
  );

  const { inVariant1: inFeaturedInsightsForYouExperiment } = useAccountExperiment(
    'atv_start_page_post_setup_v2',
    currentSite?.account,
    isTrackingCodeInstalled,
  );

  const { inVariant1: inGuidedSetupExperiment } = useAccountExperiment(
    'av_guided_setup_v2',
    currentSite?.account,
    !isTrackingCodeInstalled && !platformDetected,
  );

  const { data: beamerPosts } = useGetBeamerPosts(
    i18n.resolvedLanguage ?? 'en',
    inFeaturedInsightsForYouExperiment,
  );

  const renderTCSection = () => {
    if (!currentSite) return null;

    return (
      <TCSectionWrapper>
        {platformDetected ? (
          <CmsCard site={currentSite} platform={detectedCms} isPlatformGtm={gtm.detected} />
        ) : (
          <>
            {inGuidedSetupExperiment ? (
              <GuidedSetupCard site={currentSite} />
            ) : (
              <TrackingCodeCard site={currentSite} />
            )}
          </>
        )}
      </TCSectionWrapper>
    );
  };

  useEffect(() => {
    if (currentUser?.isDemoUser) {
      showAppcuesContent(DEMO_ACCOUNT_DAHSBOARD_ONBOARDING_ID, true);
    }
  }, [currentUser]);

  if (!currentSiteId) {
    return (
      <Container>
        <Loader />
      </Container>
    );
  }

  const shouldShowDemoBanner = !currentUser?.isDemoUser && isDemoSiteId(currentSiteId);

  return (
    <ErrorBoundary squadSentryTag="activation">
      <Container>
        <Content>
          {shouldShowDemoBanner ? (
            <FullContentWidth>
              <DemoSiteAlertBanner
                isTrackingCodeInstalled={isTrackingCodeInstalled}
                currentSiteId={currentSiteId}
              />
            </FullContentWidth>
          ) : null}
          <InnerContent>
            <HeaderWrapper>
              <Text as="h1" textType="header4" textColor="text_default">
                {/* TODO: update translations to remove workspace related content */}
                {t('activation:space-overview.title')}
              </Text>
              <SharingModule
                trigger={
                  <Button size="s" kind="secondary">
                    {t('common:actions.share')}
                  </Button>
                }
              />
            </HeaderWrapper>
            {!isTrackingCodeInstalled ? (
              <>
                <FullContentWidth>{renderTCSection()}</FullContentWidth>
                <Column>
                  <ProductDemoCard />
                </Column>
                <Column>
                  <FaqsCard />
                </Column>
              </>
            ) : null}

            {isTrackingCodeInstalled && !isDemoSiteId(currentSiteId) ? (
              <ProductTourGuideCard />
            ) : null}

            {currentSite ? <SuggestionsBox site={currentSite} /> : null}
            {postInstallCardVisible ? (
              <FullContentWidth>
                <PostInstallCard />
              </FullContentWidth>
            ) : null}

            {inFeaturedInsightsForYouExperiment ? (
              <FullContentWidth>
                <FeaturedInsights />
              </FullContentWidth>
            ) : null}
            {isTrackingCodeInstalled ? (
              <>
                <Column>
                  <RecordingsCard inForYouExperiment={inFeaturedInsightsForYouExperiment} />
                  <FeedbackCard inForYouExperiment={inFeaturedInsightsForYouExperiment} />
                </Column>
                <Column>
                  <HeatmapsCard inForYouExperiment={inFeaturedInsightsForYouExperiment} />
                  <SurveysCard inForYouExperiment={inFeaturedInsightsForYouExperiment} />
                </Column>
                {inFeaturedInsightsForYouExperiment && !!beamerPosts?.length ? (
                  <Column>
                    <NewFeaturesSection posts={beamerPosts} />
                  </Column>
                ) : null}
                {inFeaturedInsightsForYouExperiment ? (
                  <Column>
                    <HotjarTipsSection />
                  </Column>
                ) : null}
              </>
            ) : (
              <FullContentWidth>
                <QuickLinksSection
                  site={currentSite}
                  inGuidedSetupExperiment={inGuidedSetupExperiment}
                />
              </FullContentWidth>
            )}
          </InnerContent>
        </Content>
        <Sidebar />
      </Container>
    </ErrorBoundary>
  );
}
